import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  SimpleGrid,
  Flex,
  useColorModeValue,
  Spinner,
  Card,
  CardBody,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { SectionLabel } from './DashboardWrapper';
import AnimatedBox from '../AnimatedBox';
import BusinessReviewModal from '../BusinessReviewModal';
import {
  MarketMomentumMap,
  MarketHeatGauge,
  VelocityBarChart,
} from '../MarketDataVisualizations';
import {
  FaSmile,
  FaMeh,
  FaFrown,
  FaThumbsUp,
  FaExclamationTriangle,
  FaStar,
  FaQuoteLeft,
  FaLightbulb,
  FaRegStar,
  FaArrowRight,
} from 'react-icons/fa';
import { getYearData } from '../../services/yearService';
import { getTrafficValue, sortBusinessesBySeoScore } from '../../utils/competitiveIntelHelpers';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Line,
  LineChart,
} from 'recharts';

// Color constants
const COLORS = {
  primary: '#0066ff',
  success: '#48BB78',
  warning: '#ECC94B',
  danger: '#F56565',
  info: '#9F7AEA',
};

const MarketTrendsDashboard = ({ reviewsData, dateRange, demoMode, demoData }) => {
  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // State
  const [yearData, setYearData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch year data
  useEffect(() => {
    let mounted = true;

    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setIsLoading(false);
      return;
    }

    const fetchYearData = async () => {
      try {
        const data = await getYearData();
        if (mounted) {
          setYearData(data);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching year data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchYearData();

    return () => {
      mounted = false;
    };
  }, []);

  // Extract ALL market_trends_insights from all businesses
  const allMarketInsights = useMemo(() => {
    const insights = [];
    
    // Collect from reviewsData
    if (reviewsData && reviewsData.length > 0) {
      for (const business of reviewsData) {
        if (business.market_trends_insight) {
          insights.push({
            insight: business.market_trends_insight,
            businessName: business.name,
          });
        }
      }
    }

    // Also collect from yearData (might have different/additional insights)
    if (yearData && yearData.length > 0) {
      for (const yd of yearData) {
        const insight = yd.market_trends_insight || yd.data?.market_trends_insight;
        if (insight) {
          // Avoid duplicates
          const alreadyAdded = insights.some(i => i.insight === insight);
          if (!alreadyAdded) {
            insights.push({
              insight: insight,
              businessName: yd.name,
            });
          }
        }
      }
    }

    return insights;
  }, [yearData, reviewsData]);

  // Keep macroInsight for backward compatibility (uses first insight found)
  const macroInsight = useMemo(() => {
    return allMarketInsights.length > 0 ? allMarketInsights[0].insight : null;
  }, [allMarketInsights]);

  // Calculate current month metrics from yearData
  const monthlyMetrics = useMemo(() => {
    if (!yearData || yearData.length === 0) {
      return {
        currentMonthReviews: 0,
        currentMonthRating: 0,
        currentMonthSentiment: 0,
        totalBusinesses: 0,
        monthlyTrendData: [],
        topBusinessesThisMonth: [],
        businessSpotlight: [],
        sortedByTotal: [],
        sortedBySentiment: [],
        sortedByGrowth: [],
        sortedByTraffic: [],
        sortedBySeo: [],
      };
    }

    // Get current month (use month -1 since November data doesn't exist yet)
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;

    // Get last 6 months (ending with current month which is month -1)
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      last6Months.push(key);
    }

    // Aggregate data from yearData
    let currentMonthReviews = 0;
    let currentMonthPositive = 0;
    let currentMonthNeutral = 0;
    let currentMonthNegative = 0;
    let totalRating = 0;
    let ratingCount = 0;

    const businessMonthlyData = [];
    const monthlyAggregates = {};

    yearData.forEach((business) => {
      const monthlyReviewCounts = business.data?.monthlyReviewCounts || {};
      const monthlyRatings = business.data?.monthlyRatings || {};

      // Get current month data for this business
      const currentReviews = monthlyReviewCounts[currentMonthKey] || 0;
      const currentRatings = monthlyRatings[currentMonthKey] || { positive: 0, neutral: 0, negative: 0 };

      currentMonthReviews += currentReviews;
      currentMonthPositive += currentRatings.positive || 0;
      currentMonthNeutral += currentRatings.neutral || 0;
      currentMonthNegative += currentRatings.negative || 0;

      // Calculate sentiment score for this business
      const businessSentimentData = business.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const totalSentimentReviews = businessSentimentData.positive + businessSentimentData.neutral + businessSentimentData.negative;
      const sentimentScore = totalSentimentReviews > 0
        ? ((businessSentimentData.positive + businessSentimentData.neutral * 0.5) / totalSentimentReviews) * 100
        : 0;

      // Calculate growth (last month vs previous month)
      const months = Object.keys(monthlyReviewCounts).sort();
      const lastMonth = months[months.length - 1];
      const prevMonth = months[months.length - 2];
      const lastMonthCount = monthlyReviewCounts[lastMonth] || 0;
      const prevMonthCount = monthlyReviewCounts[prevMonth] || 0;
      const monthlyGrowth = lastMonthCount - prevMonthCount;

      // Calculate relative growth (growth as % of total reviews)
      const totalReviews = business.data?.totalReviews || 0;
      const relativeGrowth = totalReviews > 0 ? (monthlyGrowth / totalReviews) * 100 : 0;

      if (currentReviews > 0) {
        businessMonthlyData.push({
          name: business.name,
          maps_uri: business.maps_uri,
          reviewsThisMonth: currentReviews,
          sentimentThisMonth: currentRatings,
          averageRating: business.data?.averageRating || 0,
          totalReviews: totalReviews,
          monthlyReviewCounts,
          sentimentScore,
          monthlyGrowth,
          relativeGrowth,
        });
      }

      // Aggregate for trend chart
      last6Months.forEach((monthKey) => {
        if (!monthlyAggregates[monthKey]) {
          monthlyAggregates[monthKey] = {
            month: monthKey,
            reviews: 0,
            positive: 0,
            neutral: 0,
            negative: 0,
          };
        }

        const reviews = monthlyReviewCounts[monthKey] || 0;
        const ratings = monthlyRatings[monthKey] || { positive: 0, neutral: 0, negative: 0 };

        monthlyAggregates[monthKey].reviews += reviews;
        monthlyAggregates[monthKey].positive += ratings.positive || 0;
        monthlyAggregates[monthKey].neutral += ratings.neutral || 0;
        monthlyAggregates[monthKey].negative += ratings.negative || 0;
      });

      // Average rating
      if (business.data?.averageRating) {
        totalRating += business.data.averageRating;
        ratingCount++;
      }
    });

    const currentMonthTotal = currentMonthPositive + currentMonthNeutral + currentMonthNegative;
    const currentMonthSentiment = currentMonthTotal > 0
      ? ((currentMonthPositive + currentMonthNeutral * 0.5) / currentMonthTotal) * 100
      : 0;

    const currentMonthRating = ratingCount > 0 ? totalRating / ratingCount : 0;

    // Sort businesses by reviews this month
    const topBusinessesThisMonth = businessMonthlyData
      .sort((a, b) => b.reviewsThisMonth - a.reviewsThisMonth)
      .slice(0, 3);

    const businessSpotlight = businessMonthlyData
      .sort((a, b) => b.reviewsThisMonth - a.reviewsThisMonth)
      .slice(0, 9);

    // Create ranking arrays for BusinessReviewModal
    const sortedByTotal = [...businessMonthlyData].sort((a, b) => b.totalReviews - a.totalReviews);
    const sortedBySentiment = [...businessMonthlyData].sort((a, b) => b.sentimentScore - a.sentimentScore);
    const sortedByGrowth = [...businessMonthlyData].sort((a, b) => b.monthlyGrowth - a.monthlyGrowth);
    
    // Sort by traffic using the original yearData to get traffic values
    const sortedByTraffic = [...businessMonthlyData]
      .map((business) => {
        const originalBusiness = yearData.find(b => b.maps_uri === business.maps_uri);
        const trafficValue = originalBusiness ? (getTrafficValue(originalBusiness) || 0) : 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);

    // Sort by SEO score. Every business gets a rank — those without
    // SpyFu traffic data fall back to an estimate so the SEO category
    // never surfaces as "N/A" downstream.
    const sortedBySeo = sortBusinessesBySeoScore(
      businessMonthlyData.map((business) => {
        const originalBusiness = yearData.find(b => b.maps_uri === business.maps_uri);
        return { ...business, ...(originalBusiness || {}) };
      })
    );

    // Create trend data for chart
    const monthlyTrendData = last6Months.map((monthKey) => {
      const data = monthlyAggregates[monthKey] || { month: monthKey, reviews: 0 };
      const total = data.positive + data.neutral + data.negative;
      const sentiment = total > 0 ? ((data.positive + data.neutral * 0.5) / total) * 100 : 0;

      // Format month for display (e.g., "2025-05" -> "May")
      const [year, month] = monthKey.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString('en-US', { month: 'short' });

      return {
        month: monthName,
        monthKey,
        reviews: data.reviews,
        sentiment: sentiment.toFixed(1),
        isCurrentMonth: monthKey === currentMonthKey,
      };
    });

    return {
      currentMonthReviews,
      currentMonthRating,
      currentMonthSentiment,
      totalBusinesses: yearData.length,
      monthlyTrendData,
      topBusinessesThisMonth,
      businessSpotlight,
      sortedByTotal,
      sortedBySentiment,
      sortedByGrowth,
      sortedByTraffic,
      sortedBySeo,
    };
  }, [yearData]);

  // Calculate Market Momentum Map data (Gross Growth vs Gross Acceleration)
  const momentumMapData = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0 || !yearData || yearData.length === 0) {
      return [];
    }

    // Get current month key for gross growth
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const prevMonthKey = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}`;

    return reviewsData
      .map(business => {
        const yearBusiness = yearData.find(
          y => y.name === business.name || y.maps_uri === business.maps_uri
        );
        const monthlyData = yearBusiness?.data?.monthlyReviewCounts || {};
        const currentReviews = monthlyData[currentMonthKey] || 0;
        const prevReviews = monthlyData[prevMonthKey] || 0;
        const grossGrowth = currentReviews;
        const grossAcceleration = currentReviews - prevReviews;
        const totalReviews = business.rating_count || yearBusiness?.data?.totalReviews || 0;

        return {
          name: business.name,
          maps_uri: business.maps_uri,
          grossGrowth,
          grossAcceleration,
          totalReviews,
          isAscending: grossAcceleration > 0 && grossGrowth > 0,
        };
      })
      .filter(d => d.grossGrowth > 0 || d.grossAcceleration !== 0)
      .sort((a, b) => b.grossGrowth - a.grossGrowth)
      .slice(0, 20); // Top 20 businesses by gross growth
  }, [reviewsData, yearData]);

  // Calculate month-over-month comparison (last month vs the month before)
  const marketDemandScore = useMemo(() => {
    if (!yearData || yearData.length === 0) {
      return { score: 50, change: 0 };
    }

    // Get last month and the month before that
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
    const monthBeforeLast = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const monthBeforeLastKey = `${monthBeforeLast.getFullYear()}-${String(monthBeforeLast.getMonth() + 1).padStart(2, '0')}`;

    // Calculate total reviews for each month across all businesses
    let totalLastMonthReviews = 0;
    let totalMonthBeforeReviews = 0;

    yearData.forEach(business => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      totalLastMonthReviews += monthlyData[lastMonthKey] || 0;
      totalMonthBeforeReviews += monthlyData[monthBeforeLastKey] || 0;
    });

    // Calculate percentage change from month before to last month
    const percentChange = totalMonthBeforeReviews > 0 
      ? ((totalLastMonthReviews - totalMonthBeforeReviews) / totalMonthBeforeReviews) * 100 
      : 0;

    // Convert percentage change to a 0-100 gauge score
    // -50% or worse maps to 0, +50% or better maps to 100, 0% maps to 50
    const gaugeScore = Math.min(100, Math.max(0, 50 + percentChange));

    return { score: Math.round(gaugeScore), change: Math.round(percentChange) };
  }, [yearData]);

  // Parse ALL market_trends_insights into 5-star and 1-star review collections
  // Aggregates reviews from ALL businesses to ensure diverse representation
  const reviewInsights = useMemo(() => {
    if (!allMarketInsights || allMarketInsights.length === 0) {
      return {
        fiveStarReviews: [],
        oneStarReviews: [],
        fiveStarThemes: [],
        oneStarThemes: [],
      };
    }

    const fiveStarReviews = [];
    const oneStarReviews = [];
    const fiveStarThemes = [];
    const oneStarThemes = [];
    const seenBusinesses5Star = new Set();
    const seenBusinesses1Star = new Set();

    // Helper function to parse quotes from a line
    const parseQuotes = (quotesContent, targetArray, seenBusinesses) => {
      if (!quotesContent) return;
      
      // Split on patterns that separate quotes
      const quoteEntries = quotesContent.split(/,\s*(?=")/);
      
      quoteEntries.forEach((entry) => {
        // Try multiple patterns to match quotes
        const patterns = [
          /"([^"]+)"\s*[-–—]\s*([^(,]+?)(?:\s*\(Review ID:.*?\))?\s*$/,
          /"([^"]+)"\s*[-–—]\s*([^,]+)/,
          /[""]([^""]+)[""]\s*[-–—]\s*([^(,]+)/,
        ];
        
        for (const pattern of patterns) {
          const match = entry.match(pattern);
          if (match) {
            const quoteText = match[1].trim();
            const businessName = match[2].trim().replace(/\s*\(.*\)$/, '');
            if (quoteText && businessName && quoteText.length > 10) {
              // Prioritize businesses we haven't seen yet for diversity
              const review = { text: quoteText, business: businessName };
              if (!seenBusinesses.has(businessName)) {
                targetArray.unshift(review); // Add to front for priority
                seenBusinesses.add(businessName);
              } else {
                targetArray.push(review);
              }
            }
            break;
          }
        }
      });
    };

    // Process each business's market_trends_insight
    for (const { insight } of allMarketInsights) {
      if (!insight) continue;

      const lines = insight.split('\n');
      let currentSection = null;

      for (const line of lines) {
        const trimmed = line.trim();
        const lowerLine = trimmed.toLowerCase();

        // Detect section changes - look for various patterns
        if (lowerLine.includes('**good:**') || 
            lowerLine.includes('**working:**') ||
            lowerLine.includes('positive') && lowerLine.includes(':')) {
          currentSection = 'positive';
          // Extract theme from the line if present
          const content = trimmed.replace(/.*\*\*(good|working):\*\*/i, '').trim();
          if (content && !content.startsWith('*') && content.length > 5) {
            fiveStarThemes.push(content);
          }
          continue;
        } else if (lowerLine.includes('**bad:**') || 
                   lowerLine.includes('**not working:**') ||
                   lowerLine.includes('negative') && lowerLine.includes(':') ||
                   lowerLine.includes('complaints') ||
                   lowerLine.includes('issues')) {
          currentSection = 'negative';
          const content = trimmed.replace(/.*\*\*(bad|not working):\*\*/i, '').trim();
          if (content && !content.startsWith('*') && content.length > 5) {
            oneStarThemes.push(content);
          }
          continue;
        } else if (lowerLine.includes('**changing:**') || 
                   lowerLine.includes('**trends:**') ||
                   lowerLine.includes('**market overview:**') ||
                   lowerLine.includes('**strategies')) {
          currentSection = null; // Reset on section headers
          continue;
        }

        // Parse quotes from *Data:* lines
        if (currentSection && (lowerLine.includes('*data:*') || lowerLine.includes('data:'))) {
          const quotesContent = trimmed.replace(/.*\*?data:\*?/i, '').trim();
          if (currentSection === 'positive') {
            parseQuotes(quotesContent, fiveStarReviews, seenBusinesses5Star);
          } else if (currentSection === 'negative') {
            parseQuotes(quotesContent, oneStarReviews, seenBusinesses1Star);
          }
          continue;
        }

        // Also check for inline quotes that might not have *Data:* prefix
        if (currentSection && trimmed.includes('"') && trimmed.includes('-')) {
          if (currentSection === 'positive') {
            parseQuotes(trimmed, fiveStarReviews, seenBusinesses5Star);
          } else if (currentSection === 'negative') {
            parseQuotes(trimmed, oneStarReviews, seenBusinesses1Star);
          }
        }

        // Parse insight themes (bullet points)
        if (currentSection && trimmed.startsWith('*') && 
            !lowerLine.includes('*examples:*') && 
            !lowerLine.includes('*data:*') &&
            !lowerLine.includes('**')) {
          const content = trimmed.replace(/^\*\s*/, '').replace(/\*\*/g, '').trim();
          if (content && content.length > 5 && !content.includes('"')) {
            if (currentSection === 'positive') {
              fiveStarThemes.push(content);
            } else if (currentSection === 'negative') {
              oneStarThemes.push(content);
            }
          }
        }
      }
    }

    // Remove duplicate reviews by text
    const uniqueFiveStar = fiveStarReviews.filter((review, index, self) => 
      index === self.findIndex(r => r.text === review.text)
    );
    const uniqueOneStar = oneStarReviews.filter((review, index, self) => 
      index === self.findIndex(r => r.text === review.text)
    );

    // Remove duplicate themes
    const uniqueFiveStarThemes = [...new Set(fiveStarThemes)].slice(0, 5);
    const uniqueOneStarThemes = [...new Set(oneStarThemes)].slice(0, 5);

    // Log for debugging
    console.log('Market Insights - 5-star reviews:', uniqueFiveStar.length, 'from businesses:', [...seenBusinesses5Star]);
    console.log('Market Insights - 1-star reviews:', uniqueOneStar.length, 'from businesses:', [...seenBusinesses1Star]);

    return {
      fiveStarReviews: uniqueFiveStar.slice(0, 12), // Show up to 12 reviews
      oneStarReviews: uniqueOneStar.slice(0, 12),
      fiveStarThemes: uniqueFiveStarThemes,
      oneStarThemes: uniqueOneStarThemes,
    };
  }, [allMarketInsights]);

  // Prepare data for the review leaders bar chart
  const reviewLeadersData = useMemo(() => {
    if (!yearData || yearData.length === 0) return [];
    
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const prevMonthKey = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}`;

    return yearData
      .map(business => {
        const monthlyData = business.data?.monthlyReviewCounts || {};
        const currentReviews = monthlyData[currentMonthKey] || 0;
        const prevReviews = monthlyData[prevMonthKey] || 0;
        return {
          name: business.name,
          value: currentReviews,
          growth: currentReviews - prevReviews,
          maps_uri: business.maps_uri,
        };
      })
      .filter(b => b.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [yearData]);


  // Handle business click from various sources
  const handleBusinessClick = (business) => {
    // Find full business data from reviewsData (current snapshot)
    const currentBusinessData = reviewsData?.find(
      (b) => b.name === business.name || b.maps_uri === business.maps_uri
    );

    // Find historical data from yearData (monthly data)
    const historicalBusinessData = yearData?.find(
      (b) => b.name === business.name || b.maps_uri === business.maps_uri
    );

    // Merge current data with historical data for the modal
    const mergedBusinessData = {
      ...(currentBusinessData || business),
      // Add historical data structure that BusinessReviewModal expects
      historicalData: historicalBusinessData?.data || null,
      // Also pass the raw yearData object for compatibility
      data: historicalBusinessData?.data || null,
    };

    setSelectedBusiness(mergedBusinessData);
    setIsModalOpen(true);
  };


  // Business Spotlight Card
  const BusinessSpotlightCard = ({ business, index }) => {
    const sentiment = business.sentimentThisMonth;
    const sentimentScore = sentiment
      ? ((sentiment.positive + sentiment.neutral * 0.5) /
         (sentiment.positive + sentiment.neutral + sentiment.negative)) * 100
      : 0;

    const sentimentIcon = sentimentScore >= 70 ? FaSmile : sentimentScore >= 50 ? FaMeh : FaFrown;
    const sentimentColor = sentimentScore >= 70 ? 'green.500' : sentimentScore >= 50 ? 'yellow.500' : 'red.500';

    // Create sparkline data (last 6 months)
    const sparklineData = [];
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    for (let i = 5; i >= 0; i--) {
      const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const count = business.monthlyReviewCounts[key] || 0;
      sparklineData.push({ month: key, count });
    }

    return (
      <AnimatedBox animation="fadeIn" delay={0.4 + (index % 3) * 0.05} duration={0.4}>
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          shadow="sm"
          transition="all 0.3s"
          _hover={{ transform: 'translateY(-2px)', shadow: 'md', cursor: 'pointer' }}
          onClick={() => handleBusinessClick(business)}
          h="100%"
        >
          <CardBody p={4}>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="sm" noOfLines={1} flex={1} color="gray.800">
                  {business.name}
                </Text>
                <Icon as={sentimentIcon} color={sentimentColor} boxSize={4} />
              </HStack>

              <HStack spacing={3}>
                <VStack spacing={0} align="flex-start">
                  <Text fontSize="xl" fontWeight="bold" color="purple.500">
                    {business.reviewsThisMonth}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    this month
                  </Text>
                </VStack>

                <Box flex={1} h="40px">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparklineData}>
                      <defs>
                        <linearGradient id={`sparkline-${business.name.replace(/\s+/g, '')}`} x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.7}/>
                          <stop offset="100%" stopColor={COLORS.primary} stopOpacity={1}/>
                        </linearGradient>
                        <filter id={`sparklineGlow-${business.name.replace(/\s+/g, '')}`} x="-20%" y="-20%" width="140%" height="140%">
                          <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                          <feMerge>
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                          </feMerge>
                        </filter>
                      </defs>
                      <Line
                        type="natural"
                        dataKey="count"
                        stroke={`url(#sparkline-${business.name.replace(/\s+/g, '')})`}
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        dot={false}
                        filter={`url(#sparklineGlow-${business.name.replace(/\s+/g, '')})`}
                        isAnimationActive={true}
                        animationDuration={800}
                        animationEasing="ease-out"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </AnimatedBox>
    );
  };

  if (isLoading) {
    return (
      <Box>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Your monthly performance snapshot. See review activity, sentiment shifts, and competitive movement all in one place.">
          Month in Review
        </SectionLabel>}

        <Flex justify="center" align="center" minH="500px" width="100%">
          <VStack spacing={4}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
            <Text color="gray.600" fontSize="lg">
              Loading market trends...
            </Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Your monthly performance snapshot. See review activity, sentiment shifts, and competitive movement all in one place.">
          Month in Review
        </SectionLabel>}

        <Box p={4}>
          <Text>No market data available</Text>
        </Box>
      </Box>
    );
  }

  const sentimentColor =
    monthlyMetrics.currentMonthSentiment >= 70
      ? 'green'
      : monthlyMetrics.currentMonthSentiment >= 50
      ? 'yellow'
      : 'red';

  return (
    <Box>
      {/* Section Label */}
      {!demoMode && <SectionLabel description="Your monthly performance snapshot. See review activity, sentiment shifts, and competitive movement all in one place.">
        Month in Review
      </SectionLabel>}

      {/* Compact Dashboard Header */}
      <AnimatedBox animation="fadeIn" delay={0} duration={0.4}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="lg" color="gray.800">
              {new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Heading>
          </Box>
          {/* Compact Stats Row */}
          <HStack spacing={6} display={{ base: 'none', md: 'flex' }}>
            <VStack spacing={0} align="center">
              <Text fontSize="2xl" fontWeight="bold" color="blue.600">{monthlyMetrics.totalBusinesses}</Text>
              <Text fontSize="xs" color="gray.500">Businesses</Text>
            </VStack>
            <VStack spacing={0} align="center">
              <Text fontSize="2xl" fontWeight="bold" color="green.600">{monthlyMetrics.currentMonthReviews}</Text>
              <Text fontSize="xs" color="gray.500">Reviews</Text>
            </VStack>
            <VStack spacing={0} align="center">
              <Text fontSize="2xl" fontWeight="bold" color="purple.600">{monthlyMetrics.currentMonthRating.toFixed(1)}</Text>
              <Text fontSize="xs" color="gray.500">Avg Rating</Text>
            </VStack>
            <VStack spacing={0} align="center">
              <Text fontSize="2xl" fontWeight="bold" color={sentimentColor + '.600'}>{monthlyMetrics.currentMonthSentiment.toFixed(0)}%</Text>
              <Text fontSize="xs" color="gray.500">Positive</Text>
            </VStack>
          </HStack>
        </Flex>
      </AnimatedBox>

      <VStack spacing={6} align="stretch">
        {/* Row 1: Heat Gauge + Momentum Map */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          {/* Market Heat Index (Radial Gauge) */}
          <AnimatedBox animation="fadeIn" delay={0.1} duration={0.4}>
            <MarketHeatGauge
              score={marketDemandScore.score}
              change={marketDemandScore.change}
              label="Market Demand"
            />
          </AnimatedBox>

          {/* Market Momentum Map (Bubble Chart) - Takes 2 columns */}
          <Box gridColumn={{ lg: 'span 2' }}>
            <AnimatedBox animation="fadeIn" delay={0.15} duration={0.4}>
              {momentumMapData.length > 0 ? (
                <MarketMomentumMap
                  data={momentumMapData}
                  onBubbleClick={(business) => {
                    const fullBusiness = reviewsData?.find(
                      b => b.maps_uri === business.maps_uri || b.name === business.name
                    );
                    if (fullBusiness) {
                      handleBusinessClick(fullBusiness);
                    }
                  }}
                  height={280}
                />
              ) : (
                <Box
                  bg={cardBg}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={6}
                  h="280px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text color="gray.500">No competitive intel data available</Text>
                </Box>
              )}
            </AnimatedBox>
          </Box>
        </SimpleGrid>

        {/* Row 2: Review Leaders Bar Chart + 6-Month Trend */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Review Leaders - Velocity Bar Chart */}
          {reviewLeadersData.length > 0 && (
            <AnimatedBox animation="fadeIn" delay={0.2} duration={0.4}>
              <Box
                bg={cardBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                p={5}
              >
                <HStack justify="space-between" mb={4}>
                  <Box>
                    <Text fontSize="md" fontWeight="600" color="gray.800">
                      Review Leaders
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Most active this month
                    </Text>
                  </Box>
                </HStack>
                <Box maxH="320px" overflowY="auto">
                  <VelocityBarChart
                    data={reviewLeadersData}
                    onBarClick={(item) => {
                      const business = yearData?.find(b => b.maps_uri === item.maps_uri);
                      if (business) handleBusinessClick(business);
                    }}
                    metricLabel="reviews"
                  />
                </Box>
              </Box>
            </AnimatedBox>
          )}

          {/* 6-Month Review Volume Trend */}
          {monthlyMetrics.monthlyTrendData.length > 0 && (
            <AnimatedBox animation="fadeIn" delay={0.25} duration={0.4}>
              <Box
                bg={cardBg}
                p={5}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <HStack justify="space-between" mb={4}>
                  <Box>
                    <Text fontSize="md" fontWeight="600" color="gray.800">
                      6-Month Trend
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Review volume over time
                    </Text>
                  </Box>
                </HStack>

                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={monthlyMetrics.monthlyTrendData}>
                    <defs>
                      <linearGradient id="colorReviews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="month"
                      stroke="#666"
                      style={{ fontSize: '11px' }}
                    />
                    <YAxis stroke="#666" style={{ fontSize: '11px' }} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '8px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="reviews"
                      stroke={COLORS.primary}
                      strokeWidth={2}
                      fill="url(#colorReviews)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </AnimatedBox>
          )}
        </SimpleGrid>

        {/* Market Insights - Focused on 5-star and 1-star reviews */}
        {allMarketInsights.length > 0 && (
          <Box>
            <AnimatedBox animation="fadeIn" delay={0.15} duration={0.4}>
              <HStack spacing={3} mb={8} align="center">
                <Box w="3px" h="28px" bgGradient="linear(to-b, blue.400, purple.500)" borderRadius="full" />
                <Heading size="lg" color="gray.800" fontWeight="700" letterSpacing="-0.02em">
                  Market Insights
                </Heading>
                <Badge
                  colorScheme="purple"
                  variant="subtle"
                  fontSize="xs"
                  px={2.5}
                  py={0.5}
                  borderRadius="full"
                  fontWeight="600"
                  textTransform="none"
                >
                  Last Month
                </Badge>
              </HStack>
            </AnimatedBox>

            <VStack spacing={6} align="stretch">

              {/* Five Star Reviews Section */}
              <AnimatedBox animation="fadeIn" delay={0.2} duration={0.4}>
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  overflow="hidden"
                  shadow="sm"
                  borderWidth="1px"
                  borderColor={borderColor}
                  transition="all 0.3s ease"
                  _hover={{ shadow: 'md' }}
                >
                  <Box px={6} py={5}>
                    <HStack spacing={4} justify="space-between" align="center">
                      <HStack spacing={3}>
                        <Flex
                          w="44px"
                          h="44px"
                          borderRadius="14px"
                          bgGradient="linear(135deg, #10B981, #34D399)"
                          justify="center"
                          align="center"
                          shadow="0 2px 8px rgba(16, 185, 129, 0.3)"
                        >
                          <Icon as={FaStar} color="white" boxSize={5} />
                        </Flex>
                        <Box>
                          <Text fontSize="lg" fontWeight="700" color="gray.800" lineHeight="short">
                            Positive Reviews
                          </Text>
                          <Text fontSize="sm" color="gray.500" fontWeight="400">
                            What competitors are doing right
                          </Text>
                        </Box>
                      </HStack>
                      <Badge
                        bg="green.50"
                        color="green.600"
                        fontSize="sm"
                        fontWeight="600"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {reviewInsights.fiveStarReviews.length} reviews
                      </Badge>
                    </HStack>
                  </Box>

                  <Divider borderColor={borderColor} />

                  <SimpleGrid columns={{ base: 1, lg: 5 }} spacing={0}>
                    <Box gridColumn={{ lg: 'span 3' }} p={6}>
                      {reviewInsights.fiveStarReviews.length > 0 ? (
                        <VStack spacing={3} align="stretch">
                          {reviewInsights.fiveStarReviews.map((review, idx) => (
                            <Box
                              key={idx}
                              p={4}
                              bg="gray.50"
                              borderRadius="xl"
                              transition="all 0.25s ease"
                              _hover={{ bg: 'green.50', transform: 'translateX(4px)' }}
                              position="relative"
                            >
                              <HStack spacing={3} align="start">
                                <Icon
                                  as={FaQuoteLeft}
                                  color="green.300"
                                  boxSize={3.5}
                                  mt={1}
                                  flexShrink={0}
                                  opacity={0.7}
                                />
                                <Box flex={1}>
                                  <Text fontSize="sm" color="gray.700" lineHeight="1.7" mb={2}>
                                    {review.text}
                                  </Text>
                                  <HStack spacing={2} align="center">
                                    <HStack spacing={0.5}>
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <Icon key={star} as={FaStar} color="yellow.400" boxSize={2.5} />
                                      ))}
                                    </HStack>
                                    <Text fontSize="xs" color="gray.400">
                                      &middot;
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">
                                      {review.business}
                                    </Text>
                                  </HStack>
                                </Box>
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Flex justify="center" align="center" minH="180px">
                          <VStack spacing={3}>
                            <Flex
                              w="56px"
                              h="56px"
                              borderRadius="16px"
                              bg="gray.100"
                              justify="center"
                              align="center"
                            >
                              <Icon as={FaStar} boxSize={6} color="gray.300" />
                            </Flex>
                            <Text fontSize="sm" color="gray.400" fontWeight="500">
                              No 5-star reviews found in market data
                            </Text>
                          </VStack>
                        </Flex>
                      )}
                    </Box>

                    <Box
                      gridColumn={{ lg: 'span 2' }}
                      p={6}
                      borderLeftWidth={{ lg: '1px' }}
                      borderLeftColor={borderColor}
                      bg="gray.50"
                    >
                      <VStack align="start" spacing={5}>
                        <Box>
                          <HStack spacing={2} mb={3}>
                            <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
                              Key Themes
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                            Positive patterns competitors received praise for:
                          </Text>
                        </Box>
                        
                        <VStack align="start" spacing={3} w="100%">
                          {reviewInsights.fiveStarThemes.length > 0 ? (
                            reviewInsights.fiveStarThemes.map((theme, idx) => (
                              <HStack key={idx} spacing={3} align="start" w="100%">
                                <Flex
                                  w="22px"
                                  h="22px"
                                  borderRadius="7px"
                                  bg="green.100"
                                  justify="center"
                                  align="center"
                                  flexShrink={0}
                                  mt={0.5}
                                >
                                  <Icon as={FaThumbsUp} color="green.500" boxSize={2.5} />
                                </Flex>
                                <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                                  {theme}
                                </Text>
                              </HStack>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                              Great customer service, quality products, and excellent experiences.
                            </Text>
                          )}
                        </VStack>

                        <Box
                          mt={2}
                          p={3.5}
                          bg="white"
                          borderRadius="xl"
                          w="100%"
                          shadow="0 1px 3px rgba(0,0,0,0.04)"
                          borderWidth="1px"
                          borderColor="green.100"
                        >
                          <HStack spacing={2.5} align="start">
                            <Icon as={FaLightbulb} color="yellow.400" boxSize={3.5} mt={0.5} flexShrink={0} />
                            <Text fontSize="xs" color="gray.600" lineHeight="1.6" fontWeight="500">
                              Study what's working for competitors to improve your own customer experience.
                            </Text>
                          </HStack>
                        </Box>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>
              </AnimatedBox>

              {/* One Star Reviews Section */}
              <AnimatedBox animation="fadeIn" delay={0.25} duration={0.4}>
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  overflow="hidden"
                  shadow="sm"
                  borderWidth="1px"
                  borderColor={borderColor}
                  transition="all 0.3s ease"
                  _hover={{ shadow: 'md' }}
                >
                  <Box px={6} py={5}>
                    <HStack spacing={4} justify="space-between" align="center">
                      <HStack spacing={3}>
                        <Flex
                          w="44px"
                          h="44px"
                          borderRadius="14px"
                          bgGradient="linear(135deg, #EF4444, #F87171)"
                          justify="center"
                          align="center"
                          shadow="0 2px 8px rgba(239, 68, 68, 0.3)"
                        >
                          <Icon as={FaRegStar} color="white" boxSize={5} />
                        </Flex>
                        <Box>
                          <Text fontSize="lg" fontWeight="700" color="gray.800" lineHeight="short">
                            Negative Reviews
                          </Text>
                          <Text fontSize="sm" color="gray.500" fontWeight="400">
                            Where competitors are falling short
                          </Text>
                        </Box>
                      </HStack>
                      <Badge
                        bg="red.50"
                        color="red.600"
                        fontSize="sm"
                        fontWeight="600"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {reviewInsights.oneStarReviews.length} reviews
                      </Badge>
                    </HStack>
                  </Box>

                  <Divider borderColor={borderColor} />

                  <SimpleGrid columns={{ base: 1, lg: 5 }} spacing={0}>
                    <Box gridColumn={{ lg: 'span 3' }} p={6}>
                      {reviewInsights.oneStarReviews.length > 0 ? (
                        <VStack spacing={3} align="stretch">
                          {reviewInsights.oneStarReviews.map((review, idx) => (
                            <Box
                              key={idx}
                              p={4}
                              bg="gray.50"
                              borderRadius="xl"
                              transition="all 0.25s ease"
                              _hover={{ bg: 'red.50', transform: 'translateX(4px)' }}
                              position="relative"
                            >
                              <HStack spacing={3} align="start">
                                <Icon
                                  as={FaQuoteLeft}
                                  color="red.300"
                                  boxSize={3.5}
                                  mt={1}
                                  flexShrink={0}
                                  opacity={0.7}
                                />
                                <Box flex={1}>
                                  <Text fontSize="sm" color="gray.700" lineHeight="1.7" mb={2}>
                                    {review.text}
                                  </Text>
                                  <HStack spacing={2} align="center">
                                    <HStack spacing={0.5}>
                                      <Icon as={FaStar} color="red.300" boxSize={2.5} />
                                      {[2, 3, 4, 5].map((star) => (
                                        <Icon key={star} as={FaRegStar} color="gray.300" boxSize={2.5} />
                                      ))}
                                    </HStack>
                                    <Text fontSize="xs" color="gray.400">
                                      &middot;
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">
                                      {review.business}
                                    </Text>
                                  </HStack>
                                </Box>
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Flex justify="center" align="center" minH="180px">
                          <VStack spacing={3}>
                            <Flex
                              w="56px"
                              h="56px"
                              borderRadius="16px"
                              bg="gray.100"
                              justify="center"
                              align="center"
                            >
                              <Icon as={FaRegStar} boxSize={6} color="gray.300" />
                            </Flex>
                            <Text fontSize="sm" color="gray.400" fontWeight="500">
                              No 1-star reviews found in market data
                            </Text>
                          </VStack>
                        </Flex>
                      )}
                    </Box>

                    <Box
                      gridColumn={{ lg: 'span 2' }}
                      p={6}
                      borderLeftWidth={{ lg: '1px' }}
                      borderLeftColor={borderColor}
                      bg="gray.50"
                    >
                      <VStack align="start" spacing={5}>
                        <Box>
                          <HStack spacing={2} mb={3}>
                            <Box w="6px" h="6px" borderRadius="full" bg="red.400" />
                            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
                              Pain Points
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                            Common complaints from competitor customers:
                          </Text>
                        </Box>
                        
                        <VStack align="start" spacing={3} w="100%">
                          {reviewInsights.oneStarThemes.length > 0 ? (
                            reviewInsights.oneStarThemes.map((theme, idx) => (
                              <HStack key={idx} spacing={3} align="start" w="100%">
                                <Flex
                                  w="22px"
                                  h="22px"
                                  borderRadius="7px"
                                  bg="red.100"
                                  justify="center"
                                  align="center"
                                  flexShrink={0}
                                  mt={0.5}
                                >
                                  <Icon as={FaExclamationTriangle} color="red.500" boxSize={2.5} />
                                </Flex>
                                <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                                  {theme}
                                </Text>
                              </HStack>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                              Poor customer service, long wait times, and quality issues.
                            </Text>
                          )}
                        </VStack>

                        <Box
                          mt={2}
                          p={3.5}
                          bgGradient="linear(135deg, #FEF3C7, #FDE68A)"
                          borderRadius="xl"
                          w="100%"
                          shadow="0 1px 3px rgba(0,0,0,0.04)"
                        >
                          <HStack spacing={2.5} align="start">
                            <Icon as={FaArrowRight} color="yellow.600" boxSize={3} mt={0.5} flexShrink={0} />
                            <Text fontSize="xs" color="yellow.800" lineHeight="1.6" fontWeight="600">
                              Opportunity: These complaints reveal gaps in the market you can capitalize on.
                            </Text>
                          </HStack>
                        </Box>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>
              </AnimatedBox>
            </VStack>
          </Box>
        )}

        {/* Business Spotlight Grid */}
        {monthlyMetrics.businessSpotlight.length > 0 && (
          <Box>
            <AnimatedBox animation="fadeIn" delay={0.2} duration={0.4}>
              <Heading size="lg" mb={4} color="gray.800">
                Business Spotlight
              </Heading>
            </AnimatedBox>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {monthlyMetrics.businessSpotlight.map((business, idx) => (
                <BusinessSpotlightCard key={idx} business={business} index={idx} />
              ))}
            </SimpleGrid>
          </Box>
        )}
      </VStack>

      {/* Business Review Modal */}
      {selectedBusiness && (
        <BusinessReviewModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedBusiness(null);
          }}
          business={selectedBusiness}
          yearData={yearData}
          sortedByTotal={monthlyMetrics.sortedByTotal}
          sortedBySentiment={monthlyMetrics.sortedBySentiment}
          sortedByGrowth={monthlyMetrics.sortedByGrowth}
          sortedByTraffic={monthlyMetrics.sortedByTraffic}
          sortedBySeo={monthlyMetrics.sortedBySeo}
          demoMode={demoMode}
          demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
          demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
        />
      )}
    </Box>
  );
};

export default MarketTrendsDashboard;
